/**
 * CAP-TAX-1 · hasCapability / requireCapability / useCapability
 *
 * The single read path for tenant capability flags. Reads the singleton
 * document at `tenants/{tenantId}/capabilities/current`, validates it with
 * TenantCapabilitiesSchema, and caches parsed results for 60 seconds per
 * tenant per request.
 *
 * Direct `tenantConfig.flags.xxx` checks outside `src/platform/` are
 * forbidden by CAP-TAX-1 PR-3 ESLint rule (`no-direct-flag-check`).
 */
import { useEffect, useState } from 'react';
// DataService is the only allowed adapter to firebase/firestore.
// It ships as .js; TS resolves via tsconfig allowJs.
import DataService from '../services/DataService.js';
import {
  type CapabilityFlag,
  type TenantCapabilities,
  TenantCapabilitiesSchema,
} from './capabilities';
import { CapabilityDisabledError } from './CapabilityDisabledError';

export { CapabilityDisabledError };

interface CacheEntry {
  fetchedAt: number;
  promise: Promise<TenantCapabilities | null>;
}

const CACHE_TTL_MS = 10_000;
const cache = new Map<string, CacheEntry>();

function nowMs(): number {
  return Date.now();
}

async function loadCapabilities(
  tenantId: string,
): Promise<TenantCapabilities | null> {
  const existing = cache.get(tenantId);
  if (existing && nowMs() - existing.fetchedAt < CACHE_TTL_MS) {
    return existing.promise;
  }

  const promise = (async (): Promise<TenantCapabilities | null> => {
    // Subcollection path read — DataService.getById accepts full subcollection
    // paths. The tenantId argument also passes the in-service tenant guard.
    const raw: unknown = await (DataService as {
      getById: (
        collectionPath: string,
        docId: string,
        tenantId?: string,
      ) => Promise<unknown>;
    }).getById(`tenants/${tenantId}/capabilities`, 'current', tenantId);

    if (!raw || typeof raw !== 'object') return null;

    const parsed = TenantCapabilitiesSchema.safeParse(raw);
    if (!parsed.success) {
      console.warn(
        `[hasCapability] capabilities doc failed TenantCapabilitiesSchema for tenant=${tenantId}`,
      );
      return null;
    }
    return parsed.data as TenantCapabilities;
  })();

  cache.set(tenantId, { fetchedAt: nowMs(), promise });
  return promise;
}

/**
 * Clear the per-request cache. Primarily exposed for tests and for
 * governedCapabilityWrite (PR-2) which must invalidate after every write.
 */
export function clearCapabilityCache(tenantId?: string): void {
  if (tenantId) cache.delete(tenantId);
  else cache.clear();
}

/**
 * Read whether a capability flag is enabled for the given tenant.
 * Absent docs, soft-deleted docs, and validation failures all resolve to
 * false (fail-closed).
 */
export async function hasCapability(
  tenantId: string,
  flag: CapabilityFlag,
): Promise<boolean> {
  if (!tenantId) return false;
  const caps = await loadCapabilities(tenantId);
  if (!caps || caps.softDeleted) return false;
  return caps.flags[flag] === true;
}

/**
 * Throw CapabilityDisabledError if the flag is not enabled. Used on server
 * entry points where missing capability should abort the request.
 */
export async function requireCapability(
  tenantId: string,
  flag: CapabilityFlag,
  reason?: string,
): Promise<void> {
  const enabled = await hasCapability(tenantId, flag);
  if (!enabled) {
    throw new CapabilityDisabledError(tenantId, flag, reason);
  }
}

/**
 * React hook: returns { enabled, loading } for the given flag, resolving
 * tenantId from the app store when not passed explicitly. Safe for use in
 * render paths — does not throw.
 */
let _resolveTenantId: () => string | null = () => null;

/**
 * Wire the tenantId resolver once at app bootstrap. Kept as an injection
 * point so the platform module has no direct dependency on the app store.
 */
export function configureCapabilityResolver(
  resolver: () => string | null,
): void {
  _resolveTenantId = resolver;
}

export function useCapability(
  flag: CapabilityFlag,
  explicitTenantId?: string,
): { enabled: boolean; loading: boolean } {
  const [state, setState] = useState<{ enabled: boolean; loading: boolean }>({
    enabled: false,
    loading: true,
  });

  useEffect(() => {
    let active = true;
    const tid = explicitTenantId ?? _resolveTenantId() ?? '';
    if (!tid) {
      setState({ enabled: false, loading: false });
      return () => {};
    }
    hasCapability(tid, flag)
      .then(enabled => {
        if (active) setState({ enabled, loading: false });
      })
      .catch(() => {
        if (active) setState({ enabled: false, loading: false });
      });
    return () => {
      active = false;
    };
  }, [flag, explicitTenantId]);

  return state;
}
